import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { AGERANGE, GENDER } from '../../utils/constants';

export const DATE_RANGES = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 }
];

export const EMPTY_FILTERS = {
  rangeDays: 30,
  department: '',
  location: '',
  age: '',
  gender: '',
  ethinicity: ''
};

/** Filters the admin has actually narrowed by — drives the count in the header. */
export const countActiveFilters = (filters) =>
  ['department', 'location', 'age', 'gender', 'ethinicity'].filter((key) => filters?.[key]).length +
  (filters?.rangeDays !== EMPTY_FILTERS.rangeDays ? 1 : 0);

const Section = ({ label, children }) => (
  <div className="flex flex-col gap-3">
    <div>
      <p className="text-sm font-medium text-shoorah-lightBlack dark:text-shoorah-darkBg">
        {label}
      </p>
      <div className="h-px bg-shoorah-borderGray dark:bg-shoorah-darkBgTabColor mt-2" />
    </div>
    {children}
  </div>
);

Section.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

const selectClass =
  'w-full h-10 px-3 rounded-input border border-shoorah-inputBorder dark:border-shoorah-darkBorder bg-white dark:bg-shoorah-darkBgTabColor text-sm text-shoorah-lightBlack dark:text-shoorah-darkBg appearance-none focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active';

/**
 * Dashboard filter dialog for B2B admins.
 *
 * Holds a draft copy of the filters so nothing refetches until Done is
 * pressed — the dashboard would otherwise reload on every dropdown change.
 */
const DashboardFilterModal = ({ open, onClose, filters, onApply, filterData }) => {
  const [draft, setDraft] = useState(filters);
  const doneButtonRef = useRef(null);

  // Re-seed the draft each time the dialog opens so a cancelled edit is discarded.
  useEffect(() => {
    if (open) setDraft(filters);
  }, [open, filters]);

  const setField = (key, value) => setDraft((prev) => ({ ...prev, [key]: value }));

  const activeCount = countActiveFilters(draft);

  const selects = [
    { key: 'department', label: 'All departments', options: filterData?.departments },
    { key: 'location', label: 'All locations', options: filterData?.countries },
    { key: 'ethinicity', label: 'All ethnicities', options: filterData?.ethnicities }
  ];

  return (
    <Transition.Root appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" initialFocus={doneButtonRef} onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-[200] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-lg transform overflow-hidden rounded-card border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder bg-white dark:bg-shoorah-darkBgTabColor text-left shadow-xl transition-all">
                <div className="p-6 flex flex-col gap-6 max-h-[85vh] overflow-y-auto hide-scrollbar">
                  {/* header */}
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <Dialog.Title
                        as="h3"
                        className="text-lg font-semibold text-shoorah-lightBlack dark:text-shoorah-darkBg"
                      >
                        Filter
                      </Dialog.Title>
                      <p className="text-xs text-shoorah-gray3 mt-[2px]">
                        {activeCount} filter{activeCount === 1 ? '' : 's'} applied
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={onClose}
                      aria-label="Close filters"
                      className="p-1 rounded-full text-shoorah-gray4 dark:text-shoorah-darkBg hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgTabColor focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>

                  {/* date range */}
                  <Section label="Date range">
                    <div className="flex flex-wrap gap-3">
                      {DATE_RANGES.map((range) => {
                        const selected = draft.rangeDays === range.days;
                        return (
                          <button
                            key={range.days}
                            type="button"
                            onClick={() => setField('rangeDays', range.days)}
                            className={`px-4 py-2 rounded-input text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-shoorah-active ${
                              selected
                                ? 'bg-shoorah-sidebarBackground text-white border border-shoorah-sidebarBackground'
                                : 'border border-shoorah-inputBorder text-shoorah-gray4 dark:text-shoorah-darkBg dark:border-shoorah-darkBgTabColor hover:border-shoorah-active'
                            }`}
                          >
                            {range.label}
                          </button>
                        );
                      })}
                    </div>
                  </Section>

                  {/* audience */}
                  <Section label="Audience">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selects.map((select) => (
                        <select
                          key={select.key}
                          className={selectClass}
                          value={draft[select.key]}
                          onChange={(event) => setField(select.key, event.target.value)}
                        >
                          <option value="">{select.label}</option>
                          {(select.options || []).map((option) => (
                            <option key={option} value={String(option).toLowerCase()}>
                              {option}
                            </option>
                          ))}
                        </select>
                      ))}

                      <select
                        className={selectClass}
                        value={draft.age}
                        onChange={(event) => setField('age', event.target.value)}
                      >
                        <option value="">All ages</option>
                        {AGERANGE.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.name}
                          </option>
                        ))}
                      </select>

                      <select
                        className={selectClass}
                        value={draft.gender}
                        onChange={(event) => setField('gender', event.target.value)}
                      >
                        <option value="">All genders</option>
                        {GENDER.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </Section>

                  {/* footer */}
                  <div className="flex items-center gap-3 pt-1">
                    <button
                      type="button"
                      onClick={() => setDraft(EMPTY_FILTERS)}
                      className="btn-secondary btn-sm"
                    >
                      Reset
                    </button>
                    <button
                      ref={doneButtonRef}
                      type="button"
                      onClick={() => {
                        onApply(draft);
                        onClose();
                      }}
                      className="btn-primary btn-sm"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

DashboardFilterModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  filters: PropTypes.object.isRequired,
  onApply: PropTypes.func.isRequired,
  filterData: PropTypes.shape({
    countries: PropTypes.array,
    departments: PropTypes.array,
    ethnicities: PropTypes.array
  })
};

export default DashboardFilterModal;
